import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FieldUpdateLaunchModal from './FieldUpdateLaunchModal';
import IdeaLandscape from './IdeaLandscape';
import FieldUpdateDiffReview from './FieldUpdateDiffReview';
import { fieldUpdateService, type IdeaLandscape as IdeaLandscapeType } from '../services/fieldUpdateService';

interface Props {
  subProjectId: string;
  fieldName: string;
  fieldValue: string;
  onAccept: (newText: string) => void;
  onClose: () => void;
}

type Phase = 'launch' | 'polling' | 'landscape' | 'rewriting' | 'diff' | 'error';

interface DiffState {
  proposedText: string;
  citationCount: number;
  modelUsed: string | null;
}

const PHASE_LABELS: Record<Phase, string> = {
  launch: '',
  polling: 'Building Idea Landscape…',
  landscape: '',
  rewriting: 'Generating updated draft…',
  diff: '',
  error: '',
};

const FieldUpdateFlow: React.FC<Props> = ({ subProjectId, fieldName, fieldValue, onAccept, onClose }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('launch');
  const [runId, setRunId] = useState<string | null>(null);
  const [landscape, setLandscape] = useState<IdeaLandscapeType | null>(null);
  const [diff, setDiff] = useState<DiffState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pollAttempt, setPollAttempt] = useState(0);
  const cancelledRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => { return () => { cancelledRef.current = true; }; }, []);

  // Polling loop — starts when runId is set and phase === 'polling'
  useEffect(() => {
    if (phase !== 'polling' || !runId) return;

    // Reset cancellation flag each time the effect (re-)runs
    cancelledRef.current = false;

    let timeoutId: ReturnType<typeof setTimeout>;
    let attempt = 0;
    const MAX_ATTEMPTS = 120;
    const INTERVAL = 3000;

    const poll = async () => {
      if (cancelledRef.current) return;
      if (attempt >= MAX_ATTEMPTS) {
        setError(t('fieldUpdate.pollTimeout', 'Pipeline timed out. Please try again.'));
        setPhase('error');
        return;
      }
      try {
        const res = await fieldUpdateService['_pollOnce'](subProjectId, fieldName, runId);
        if (res) {
          if (cancelledRef.current) return;
          setLandscape(res);
          setPhase('landscape');
          return;
        }
      } catch (e: unknown) {
        if (cancelledRef.current) return;
        // 202 = still running, keep polling
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 202) {
          // continue
        } else {
          const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? (e instanceof Error ? e.message : 'Pipeline failed');
          setError(msg);
          setPhase('error');
          return;
        }
      }
      attempt++;
      setPollAttempt(attempt);
      timeoutId = setTimeout(poll, INTERVAL);
    };

    poll();
    return () => clearTimeout(timeoutId);
  }, [phase, runId, subProjectId, fieldName, t]);

  const handleLaunched = useCallback(async (newRunId: string) => {
    setRunId(newRunId);
    setPhase('polling');
  }, []);

  const handleGenerateDraft = useCallback(async (selectedClusterIds: string[]) => {
    if (!runId || !landscape) return;
    setPhase('rewriting');
    try {
      const resp = await fieldUpdateService.rewrite(subProjectId, fieldName, runId, {
        run_id: runId,
        selected_cluster_ids: selectedClusterIds,
      });
      if (cancelledRef.current) return;
      setDiff({ proposedText: resp.proposed_text, citationCount: resp.citation_count, modelUsed: resp.llm_model_used });
      setPhase('diff');
    } catch (e: unknown) {
      if (cancelledRef.current) return;
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? (e instanceof Error ? e.message : 'Rewrite failed');
      setError(msg);
      setPhase('error');
    }
  }, [runId, landscape, subProjectId, fieldName]);

  const handleRefine = useCallback(async (instruction: string) => {
    if (!runId || !landscape || !diff) return;
    const selectedClusterIds = landscape.clusters
      .filter(c => c.category === 'novel')
      .map(c => c.cluster_id);
    setPhase('rewriting');
    try {
      const resp = await fieldUpdateService.rewrite(subProjectId, fieldName, runId, {
        run_id: runId,
        selected_cluster_ids: selectedClusterIds,
        refinement_instruction: instruction,
      });
      if (cancelledRef.current) return;
      setDiff({ proposedText: resp.proposed_text, citationCount: resp.citation_count, modelUsed: resp.llm_model_used });
      setPhase('diff');
    } catch (e: unknown) {
      if (cancelledRef.current) return;
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? (e instanceof Error ? e.message : 'Refinement failed');
      setError(msg);
      setPhase('diff'); // stay in diff so user can retry
    }
  }, [runId, landscape, diff, subProjectId, fieldName]);

  const handleAccept = useCallback((finalText: string) => {
    onAccept(finalText);
    onClose();
  }, [onAccept, onClose]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (phase === 'launch') {
    return (
      <FieldUpdateLaunchModal
        isOpen
        subProjectId={subProjectId}
        fieldName={fieldName}
        onClose={onClose}
        onLaunched={handleLaunched}
      />
    );
  }

  const overlayContent = () => {
    if (phase === 'polling' || phase === 'rewriting') {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-600">{PHASE_LABELS[phase]}</p>
          {phase === 'polling' && (
            <p className="text-xs text-slate-400">{t('fieldUpdate.pollAttempt', 'Checking… ({{n}})', { n: pollAttempt })}</p>
          )}
        </div>
      );
    }

    if (phase === 'landscape' && landscape) {
      return (
        <div className="p-5 overflow-y-auto flex-1">
          <IdeaLandscape
            landscape={landscape}
            onGenerateDraft={handleGenerateDraft}
            generating={false}
          />
        </div>
      );
    }

    if (phase === 'diff' && diff) {
      return (
        <div className="p-5 overflow-y-auto flex-1">
          <FieldUpdateDiffReview
            originalText={fieldValue}
            proposedText={diff.proposedText}
            citationCount={diff.citationCount}
            onAccept={handleAccept}
            onReject={() => setPhase('landscape')}
            onRefine={handleRefine}
            refining={false}
          />
        </div>
      );
    }

    if (phase === 'error') {
      return (
        <div className="p-5 space-y-4">
          <p className="text-sm text-red-600">{error}</p>
          <div className="flex gap-2">
            <button
              onClick={() => { setError(null); setPhase('launch'); setRunId(null); setLandscape(null); setDiff(null); }}
              className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
            >
              {t('common.back', 'Back')}
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  const title = () => {
    if (phase === 'polling') return t('fieldUpdate.modalTitle', 'Update field from Knowledge Base');
    if (phase === 'landscape') return t('fieldUpdate.landscapeTitle', 'Idea Landscape');
    if (phase === 'diff' || phase === 'rewriting') return t('fieldUpdate.diffTitle', 'Review proposed changes');
    if (phase === 'error') return t('fieldUpdate.errorTitle', 'Error');
    return '';
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-2">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] flex flex-col h-[95vh]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0">
          <h2 className="text-sm font-semibold text-slate-800">{title()}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>
        {overlayContent()}
      </div>
    </div>
  );
};

export default FieldUpdateFlow;
